import { useCallback, useEffect, useState } from 'react';
import { channelsAPI, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Link2, Loader2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import HelpButton from '@/components/HelpButton';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import { fechaYHora } from '@/lib/fechas';

/**
 * La cuenta de un canal de Teams, conectada una vez por alguien que la tiene.
 *
 * **Por qué (2026-10-19).** Teams leía los chats con un permiso de aplicación que alcanzaba para
 * leer los de cualquier cuenta, y contestaba con la contraseña de la cuenta guardada. Ahora un
 * administrador de Microsoft 365 habilita cada cuenta, y acá alguien que la tiene inicia sesión
 * una vez: OmniSyS guarda solo la autorización, cifrada. Si Microsoft la deja de aceptar, este
 * bloque dice «hay que volver a conectarla».
 */
export default function CuentaConectada({ channel }) {
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [yendo, setYendo] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const cuenta = channel?.config?.microsoft_account;

  const cargar = useCallback(async () => {
    if (!channel?.channel_id || !cuenta) { setCargando(false); return; }
    setCargando(true);
    try {
      const res = await channelsAPI.estadoCuentaConectada(channel.channel_id);
      setEstado(res.data);
    } catch (err) {
      setEstado({ estado: 'error', ultimo_error: formatApiError(err) });
    } finally {
      setCargando(false);
    }
  }, [channel?.channel_id, cuenta]);

  useEffect(() => { cargar(); }, [cargar]);

  const conectar = async () => {
    setYendo(true);
    try {
      const res = await channelsAPI.iniciarCuentaConectada(channel.channel_id);
      window.location.assign(res.data.url);
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo empezar la conexión');
      setYendo(false);
    }
  };

  const desconectar = async () => {
    try {
      await channelsAPI.desconectarCuentaConectada(channel.channel_id);
      toast.success('Cuenta desconectada: Teams deja de leer y contestar con ella');
      cargar();
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo desconectar');
    }
  };

  if (!cuenta) return null;
  const e = estado?.estado;

  return (
    <div className="border border-brand-border rounded-sm p-4 space-y-3" data-testid="cuenta-conectada">
      <h4 className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-1.5">
        <Link2 className="w-3.5 h-3.5" /> Cuenta conectada <HelpButton section="channels_cuenta_conectada" />
      </h4>

      {cargando ? (
        <p className="text-xs text-zinc-400 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Consultando…</p>
      ) : e === 'conectada' ? (
        <p className="text-xs text-green-700 flex items-start gap-1.5" data-testid="cuenta-conectada-ok">
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            <span className="font-mono">{cuenta}</span> conectada
            {estado.conectada_por ? <> por {estado.conectada_por}</> : null}
            {estado.conectada_at ? <> el {fechaYHora(estado.conectada_at)}</> : null}.
            Teams lee y contesta con esta cuenta.
          </span>
        </p>
      ) : e === 'requiere_reconexion' ? (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm p-2 flex items-start gap-1.5"
          data-testid="cuenta-conectada-reconectar">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            <strong>Hay que volver a conectarla.</strong> Microsoft dejó de aceptar la autorización
            {estado.ultimo_error ? <> ({estado.ultimo_error})</> : null}. Mientras tanto, Teams no lee ni contesta con esta cuenta.
          </span>
        </p>
      ) : (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 flex items-start gap-1.5"
          data-testid="cuenta-conectada-sin">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            <strong>Sin conectar.</strong> Teams no puede leer ni contestar con <span className="font-mono">{cuenta}</span> hasta
            que alguien que tenga esa cuenta la conecte.
            {e === 'error' && estado?.ultimo_error ? <> ({estado.ultimo_error})</> : null}
          </span>
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={conectar} disabled={yendo || cargando} data-testid="cuenta-conectada-conectar"
          className="rounded-sm text-xs h-8 bg-brand-primary hover:bg-[#001F6D] text-white">
          {yendo ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Link2 className="w-3 h-3 mr-1" />}
          {e === 'conectada' ? 'Volver a conectar' : 'Conectar cuenta de Microsoft'}
        </Button>
        {(e === 'conectada' || e === 'requiere_reconexion') && (
          <Button size="sm" variant="outline" onClick={() => setConfirmar(true)} data-testid="cuenta-conectada-desconectar"
            className="rounded-sm text-xs h-8">
            <Unlink className="w-3 h-3 mr-1" /> Desconectar
          </Button>
        )}
      </div>
      <p className="text-[10px] text-zinc-500">
        Se abre el inicio de sesión de Microsoft: entrá con <span className="font-mono">{cuenta}</span>. Si Microsoft dice que
        hace falta la aprobación de un administrador, esa cuenta todavía no está habilitada para OmniSyS.
      </p>

      <ConfirmActionDialog
        open={confirmar}
        onOpenChange={setConfirmar}
        title="Desconectar la cuenta"
        description={`Teams deja de leer y contestar con ${cuenta} hasta que alguien la vuelva a conectar.`}
        confirmText="Desconectar"
        onConfirm={desconectar}
      />
    </div>
  );
}
